import { Actions } from "../../constants/actions";
import { userEditForm } from "./user-edit-form";

interface UserEditHandlers {
    onEnterEdit: () => void;
    onExitEdit: () => void;
    onSubmit: (event: Event) => void;
    onKeydown: (event: KeyboardEvent) => void;
}

interface UserEditElements {
    toggleBtn: HTMLElement | null;
    cancelBtn: Element | null;
    editForm: HTMLFormElement | null;
}

interface MasterDataEditControllerLike {
    modal: HTMLElement | null;
    form: HTMLFormElement | null;
    resource: string;
    _userPrefecturePendingValue?: string;
    lookupUserAddressByZipCode(zip: string): Promise<void>;
    openUserPrefectureModal(): void;
    renderUserPrefectureDisplay(value: string): void;
    clearUserPrefecturePendingValue(): void;
    applyUserPrefecturePendingValue(): void;
    closeUserPrefectureModal(): void;
    renderUserPrefectureModalList(filterText?: string): void;
}

export class UserEditListener {
    public handlers: UserEditHandlers;

    public constructor(handlers: UserEditHandlers) {
        this.handlers = handlers;
    }

    public bind(elements: UserEditElements): void {
        elements.toggleBtn?.addEventListener("click", this.handlers.onEnterEdit);
        elements.cancelBtn?.addEventListener("click", this.handlers.onExitEdit);
        elements.editForm?.addEventListener("submit", this.handlers.onSubmit);
        elements.editForm?.addEventListener("keydown", this.handlers.onKeydown);
    }

    public static bindPrefectureChoiceChange(radio: HTMLInputElement, onChange: () => void): void {
        radio.addEventListener("change", onChange);
    }
}

export function registerUserEditMasterDataListener(controller: MasterDataEditControllerLike): void {
    const modal = controller.modal;
    const form = controller.form;
    if (controller.resource !== "users") return;
    if (!(modal instanceof HTMLElement) || !(form instanceof HTMLFormElement)) return;

    const zipEl = form.elements.namedItem("zip_code");
    const lookupBtn = userEditForm.getZipLookupButton(modal);
    if (zipEl instanceof HTMLInputElement && lookupBtn instanceof HTMLButtonElement) {
        if (lookupBtn.dataset.usersListenerInit !== "1") {
            lookupBtn.dataset.usersListenerInit = "1";
            lookupBtn.addEventListener("click", async () => {
                await controller.lookupUserAddressByZipCode(zipEl.value);
            });
        }
    }

    const pickerEl = userEditForm.getPrefecturePicker(modal);
    const clearEl = userEditForm.getPrefectureClearButton(modal);
    const overlayEl = userEditForm.getPrefectureModalOverlay(modal);
    const closeEl = userEditForm.getPrefectureModalClose(modal);
    const modalEl = userEditForm.getPrefectureModal(modal);
    const filterEl = userEditForm.getPrefectureModalFilter(modal);
    const clearModalBtn = userEditForm.getPrefectureModalActionButton(modal, Actions.CLEAR);
    const confirmModalBtn = userEditForm.getPrefectureModalActionButton(modal, Actions.CONFIRM);

    if (pickerEl instanceof HTMLElement && pickerEl.dataset.usersListenerInit !== "1") {
        pickerEl.dataset.usersListenerInit = "1";
        pickerEl.addEventListener("click", () => controller.openUserPrefectureModal());
        pickerEl.addEventListener("keydown", (event) => {
            if (!(event instanceof KeyboardEvent)) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            controller.openUserPrefectureModal();
        });
    }

    if (clearEl instanceof HTMLButtonElement && clearEl.dataset.usersListenerInit !== "1") {
        clearEl.dataset.usersListenerInit = "1";
        clearEl.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            const prefectureEl = form.elements.namedItem("prefecture");
            if (prefectureEl instanceof HTMLInputElement) {
                prefectureEl.value = "";
            }
            controller.renderUserPrefectureDisplay("");
        });
    }

    if (clearModalBtn instanceof HTMLButtonElement && clearModalBtn.dataset.usersListenerInit !== "1") {
        clearModalBtn.dataset.usersListenerInit = "1";
        clearModalBtn.addEventListener("click", (event) => {
            event.preventDefault();
            controller.clearUserPrefecturePendingValue();
        });
    }

    if (confirmModalBtn instanceof HTMLButtonElement && confirmModalBtn.dataset.usersListenerInit !== "1") {
        confirmModalBtn.dataset.usersListenerInit = "1";
        confirmModalBtn.addEventListener("click", (event) => {
            event.preventDefault();
            controller.applyUserPrefecturePendingValue();
        });
    }

    if (closeEl instanceof HTMLElement && closeEl.dataset.usersListenerInit !== "1") {
        closeEl.dataset.usersListenerInit = "1";
        closeEl.addEventListener("click", () => controller.closeUserPrefectureModal());
    }

    if (overlayEl instanceof HTMLElement && overlayEl.dataset.usersListenerInit !== "1") {
        overlayEl.dataset.usersListenerInit = "1";
        overlayEl.addEventListener("click", () => controller.closeUserPrefectureModal());
    }

    if (modalEl instanceof HTMLElement && modalEl.dataset.usersListenerInit !== "1") {
        modalEl.dataset.usersListenerInit = "1";
        modalEl.addEventListener("click", (event) => {
            if (event.target === modalEl) {
                controller.closeUserPrefectureModal();
            }
        });
    }

    if (filterEl instanceof HTMLInputElement && filterEl.dataset.usersListenerInit !== "1") {
        filterEl.dataset.usersListenerInit = "1";
        filterEl.addEventListener("input", () => {
            controller.renderUserPrefectureModalList(filterEl.value);
        });
        filterEl.addEventListener("keydown", (event) => {
            if (!(event instanceof KeyboardEvent)) return;
            if (event.key === "Escape") {
                event.preventDefault();
                controller.closeUserPrefectureModal();
            }
        });
    }

    const selected = form.elements.namedItem("prefecture");
    const selectedValue = selected instanceof HTMLInputElement ? selected.value : "";
    controller.renderUserPrefectureDisplay(selectedValue);

    if (modal.dataset.usersLanguageBound !== "1") {
        modal.dataset.usersLanguageBound = "1";
        document.addEventListener("languageChanged", () => {
            const current = form.elements.namedItem("prefecture");
            const currentValue = current instanceof HTMLInputElement ? current.value : "";
            controller.renderUserPrefectureDisplay(currentValue);
            if (controller.modal && userEditForm.isPrefectureModalVisible(controller.modal)) {
                controller.renderUserPrefectureModalList(filterEl instanceof HTMLInputElement ? filterEl.value : "");
            }
        });
    }
}
